import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Card, Col, Layout, Row, Typography } from "antd";
import {
  CheckCircleOutlined,
  ClusterOutlined,
  DashboardOutlined,
  ExperimentOutlined,
  ProjectOutlined,
  ReconciliationOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  FilePdfOutlined,
  SendOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";

import CustomHeader from "../Header";
import Footer from "../Footer";
import "./Production.css"; // reuse same styling
import { scrollToElementById } from "../../../Utility/scrollUtils";
import { Helmet } from "react-helmet-async";

const { Header, Content } = Layout;
const { Title, Paragraph } = Typography;

// --- TEXT CONTENT ---
enum ProductionText {
  HERO_TITLE = "Production Management with Ease Margin",
  HERO_DESCRIPTION = `Transform the way you plan, track, and execute production with Ease Margin, a powerful SaaS-based solution built to streamline your manufacturing processes. Whether you run single-level or multi-level production, our system ensures seamless coordination from raw material planning to finished goods testing.`,
  HERO_BUTTON = "Get Started for Free",

  INTRO_TITLE = "Smarter, Automated Production Workflows",
  RAW_TITLE = "Raw Material & Process Tracking",
  PROGRESS_TITLE = "Production Progress & Monitoring",
  QUALITY_TITLE = "Quality & Testing Built In",
  FLEX_TITLE = "Flexible Production Types",
  LEVELS_TITLE = "Single-Level & Multi-Level Production",
  MACHINE_TITLE = "Machine Efficiency Tracking",

  DASHBOARD_TITLE = "📊 Your Production at a Glance",
  SHARE_TITLE = "📤 Seamless Sharing & Storage",

  ADVANTAGE_TITLE = "⭐ Why Choose Ease Margin for Production Management?",
  CTA_TITLE = "🚀 Production becomes smarter, faster, and more reliable.",
  CTA_DESCRIPTION = `With Ease Margin, production becomes smarter, faster, and more reliable — helping you focus on growth while we handle the complexity.`,
  CTA_BUTTON = "Contact Us for a Free Demo",
}

// --- FEATURE DATA ---
const sectionCards: {
  icon: React.ReactNode;
  title: string;
  points: string[];
  bg?: string;
}[] = [
    {
      icon: <ThunderboltOutlined />,
      title: ProductionText.INTRO_TITLE,
      points: [
        "Auto-Generated Work Orders from Sales Orders — zero manual handovers.",
        "Seamless integration keeps Sales, Production, and Inventory in sync.",
      ],
      bg: "#F6FFED",
    },
    {
      icon: <ReconciliationOutlined />,
      title: ProductionText.RAW_TITLE,
      points: [
        "Track raw material consumption in real time.",
        "Clear visibility into usage, wastage, and balance.",
        "Automated material planning prevents shortages.",
      ],
      bg: "#FFF1F0",
    },
    {
      icon: <DashboardOutlined />,
      title: ProductionText.PROGRESS_TITLE,
      points: [
        "Process tracking at every stage for full transparency.",
        "Monitor WIP and finished goods output.",
        "Identify bottlenecks early to meet deadlines.",
      ],
      bg: "#F9F0FF",
    },
    {
      icon: <ExperimentOutlined />,
      title: ProductionText.QUALITY_TITLE,
      points: [
        "Log and manage finished goods testing before dispatch.",
        "Ensure only quality-approved items move to sales.",
      ],
      bg: "#E6F7FF",
    },
    {
      icon: <ProjectOutlined />,
      title: ProductionText.FLEX_TITLE,
      points: [
        "Make-to-Order (MTO): auto-plan production from sales orders.",
        "Make-to-Stock (MTS): forecast-based production and optimal stock levels.",
        "Manage both types in one platform.",
      ],
      bg: "#FFFBE6",
    },
    {
      icon: <ClusterOutlined />,
      title: ProductionText.LEVELS_TITLE,
      points: [
        "Handle simple one-step or complex multi-level BOM workflows.",
        "Assign tasks efficiently across multiple stages.",
      ],
      bg: "#F6FFED",
    },
    {
      icon: <ToolOutlined />,
      title: ProductionText.MACHINE_TITLE,
      points: [
        "Measure machine performance and utilization.",
        "Spot inefficiencies and maximize throughput.",
      ],
      bg: "#E6F7FF",
    },
  ];

const productionDashboardFeatures = [
  "Real-time WIP widgets",
  "BOM variance & consumption summaries",
  "Pending approvals & exception alerts",
  "Station-wise / machine-wise throughput",
  "On-time completion vs plan",
];

const sharingAndStorage = [
  {
    icon: <FilePdfOutlined style={{ marginRight: 8, color: "#f5222d" }} />,
    text: "Download",
  },
  { icon: "🖨️", text: "Print directly" },
  {
    icon: <SendOutlined style={{ marginRight: 8, color: "#1890ff" }} />,
    text: "Send via Email",
  },
  {
    icon: <WhatsAppOutlined style={{ marginRight: 8, color: "#25D366" }} />,
    text: "Send via WhatsApp",
  },
  { icon: "🔗", text: "Share Document Links" },
];

const advantages = [
  "Cloud-based SaaS — access anywhere",
  "End-to-end integration with Sales, Purchase, Inventory, MRP",
  "Supports Make-to-Order and Make-to-Stock",
  "Real-time insights to reduce costs, improve quality, speed delivery",
];

const ProductionManagement: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    scrollToElementById("production");
  }, [pathname]);

  return (
    <div id="production">
      <Helmet>
        <title>Production Management Software for Smarter Operations | Ease Margin</title>
        <meta
          name="description"
          content="Streamline production with Ease Margin. Plan, track, and monitor workflows, raw materials, and quality to boost efficiency and meet deadlines effortlessly."
        />
        <link
          rel="canonical" href="https://easemargin.com/services/production-management" />
      </Helmet>

      <CustomHeader />
      <Layout className="salesFeature-layout">
        {/* Hero */}
        <Header className="salesFeature-header">
          <div className="salesFeature-header-inner">
            <Title level={1} className="salesFeature-title-large">
              {ProductionText.HERO_TITLE}
            </Title>
            <Paragraph className="salesFeature-paragraph-header">
              {ProductionText.HERO_DESCRIPTION}
            </Paragraph>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange">
                Request a Demo
              </Button>
            </Link>

          </div>
        </Header>

        <Content className="salesFeature-content">
          {/* Section cards */}
          <Row gutter={[24, 24]}>
            {sectionCards.map((sec, idx) => (
              <Col xs={24} md={12} key={idx}>
                <Card
                  bordered={false}
                  className="card-default"
                  style={sec.bg ? { backgroundColor: sec.bg } : undefined}
                >
                  <Title level={4} className="salesFeature-title-flex">
                    <span style={{ marginRight: 8 }}>{sec.icon}</span>
                    {sec.title}
                  </Title>
                  {sec.points.map((p, i) => (
                    <Paragraph
                      key={i}
                      className="salesFeature-paragraph-desc"
                      style={{ marginBottom: 8 }}
                    >
                      <CheckCircleOutlined
                        style={{ color: "#1890ff", marginRight: 8 }}
                      />
                      {p}
                    </Paragraph>
                  ))}
                </Card>
              </Col>
            ))}
          </Row>

          {/* Dashboard */}
          {/* <Title level={3} className="salesFeature-title-3rd-marginTop">
            {ProductionText.DASHBOARD_TITLE}
          </Title>
          <Row gutter={[24, 24]} style={{ marginBottom: 40 }}>
            {productionDashboardFeatures.map((feature, index) => (
              <Col xs={24} sm={12} key={index}>
                <Card bordered={false} className="card-default" style={{ backgroundColor: "#F6FFED" }}>
                  <Paragraph className="salesFeature-paragraph-dashboard">
                    <CheckCircleOutlined style={{ color: "#1890ff", marginRight: 8 }} />
                    {feature}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row> */}

          {/* Sharing & storage */}
          {/* <Title level={3} className="salesFeature-title-3rd-marginTop">
            {ProductionText.SHARE_TITLE}
          </Title>
          <Row gutter={[24, 24]} style={{ marginTop: 20, marginBottom: 40 }}>
            <Col xs={24} md={12}>
              <Card bordered={false} className="card-default">
                <Title level={4} style={{ color: "#722ed1" }}>
                  📂 Generate Documents in Multiple Formats
                </Title>
                <Paragraph style={{ marginLeft: 20 }}>
                  <FilePdfOutlined style={{ marginRight: 8, color: "#fa541c" }} /> PDF
                </Paragraph>
                <Paragraph style={{ marginLeft: 20 }}>🖨️ Printable A4</Paragraph>
                <Paragraph style={{ marginLeft: 20 }}>✉️ Email-ready attachments</Paragraph>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card bordered={false} className="card-default">
                <Title level={4} style={{ color: "#722ed1" }}>
                  {ProductionText.SHARE_TITLE}
                </Title>
                <Row gutter={[0, 16]}>
                  {sharingAndStorage.map((s, i) => (
                    <Col span={12} key={i}>
                      <Paragraph style={{ marginLeft: 20 }}>
                        {typeof s.icon === "string" ? <span style={{ marginRight: 8 }}>{s.icon}</span> : s.icon}
                        {s.text}
                      </Paragraph>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          </Row> */}

          {/* Advantages */}
          <Title level={3} className="salesFeature-title-3rd-marginTop">
            {ProductionText.ADVANTAGE_TITLE}
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 40 }}>
            {advantages.map((adv, idx) => (
              <Col xs={24} sm={12} key={idx}>
                <Card
                  bordered={false}
                  className="card-default card-light"
                  style={{ backgroundColor: "#F6FFED" }}
                >
                  <Paragraph className="salesFeature-paragraph-dashboard">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {adv}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>

          {/* CTA */}
          <Card
            bordered={false}
            className="card-default salesFeature-card-footer"
          >
            <Title level={2} className="salesFeature-title-footer">
              {ProductionText.CTA_TITLE}
            </Title>
            <Paragraph className="salesFeature-paragraph-footer">
              {ProductionText.CTA_DESCRIPTION}
            </Paragraph>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange">
                {ProductionText.CTA_BUTTON}
              </Button>
            </Link>
          </Card>
        </Content>
      </Layout>
      <Footer />
    </div>
  );
};

export default ProductionManagement;
